import { type KeyboardEvent, useRef, useState } from 'react';

interface Props {
  onSend: (text: string) => Promise<void>;
  disabled?: boolean;
  disabledReason?: string;
}

export function MessageComposer({ onSend, disabled, disabledReason }: Props) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setText('');
      // Keep focus on the textarea after successfully sending
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (disabled) {
    return (
      <div className="border-t border-neutral-200 dark:border-neutral-700 px-4 py-3 bg-neutral-50 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 text-sm text-center">
        {disabledReason ?? 'No puedes enviar mensajes en este estado'}
      </div>
    );
  }

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-700 p-3 flex items-end gap-2 bg-white dark:bg-neutral-800">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKey}
        disabled={sending}
        placeholder="Escribe un mensaje… (Enter para enviar, Shift+Enter para nueva línea)"
        rows={2}
        className="flex-1 resize-none rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-850 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-white disabled:opacity-50 disabled:bg-neutral-100 dark:disabled:bg-neutral-900/50 disabled:cursor-not-allowed"
        aria-label="Mensaje al usuario"
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={!text.trim() || sending}
        className="btn btn-primary h-10 px-4 text-sm font-semibold rounded-lg shrink-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        aria-label="Enviar mensaje"
      >
        {sending ? <span className="spinner spinner-xs border-t-white" /> : 'Enviar'}
      </button>
    </div>
  );
}
